/** Shared vocabulary for the meta engine — field types, guard ops, colors. */

import { formatCurrency, formatDate, formatDateTime } from "./format";

export const FIELD_TYPES = [
    { key: "text", label: "Text" },
    { key: "textarea", label: "Long text" },
    { key: "number", label: "Number" },
    { key: "currency", label: "Currency" },
    { key: "date", label: "Date" },
    { key: "datetime", label: "Date & time" },
    { key: "boolean", label: "Yes / No" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "url", label: "URL" },
    { key: "picklist", label: "Picklist" },
    { key: "user", label: "User" },
    { key: "relation", label: "Relation (link to another object)" },
];

/** Types whose value reads as a name — the ones worth using as a label. */
export const TEXTISH_TYPES = ["text", "email", "phone", "url", "textarea", "picklist"];

/** The label a relation field shows on the field table: "Relation → Journeys". */
export const fieldTypeLabel = (field) => {
    if (field?.type === "relation") {
        const target = field.config?.objectName;
        return target ? `Relation → ${target}` : "Relation";
    }
    return (
        FIELD_TYPES.find((t) => t.key === field?.type)?.label ??
        field?.type ??
        "—"
    );
};

export const OPS = [
    { key: "eq", label: "equals" },
    { key: "neq", label: "does not equal" },
    { key: "gt", label: "is greater than" },
    { key: "gte", label: "is at least" },
    { key: "lt", label: "is less than" },
    { key: "lte", label: "is at most" },
    { key: "contains", label: "contains" },
    { key: "not_empty", label: "is not empty" },
    { key: "empty", label: "is empty" },
    { key: "in", label: "is any of" },
];

export const opNeedsValue = (op) => op !== "empty" && op !== "not_empty";

export const opLabel = (op) => OPS.find((o) => o.key === op)?.label || op;

export const STAGE_COLORS = [
    "#B8B5AF",
    "#7A9BC4",
    "#C4A24E",
    "#B07B9E",
    "#1E6B4F",
    "#8B8781",
    "#3D5A80",
    "#B3423A",
];

export const TRIGGER_TYPES = [
    { key: "record.created", label: "Record is created" },
    { key: "record.updated", label: "Record is updated" },
    { key: "record.stage_changed", label: "Record changes stage" },
    { key: "schedule", label: "On a schedule" },
];

export const triggerLabel = (trigger) => {
    const base =
        TRIGGER_TYPES.find((t) => t.key === trigger?.type)?.label ||
        trigger?.type ||
        "—";
    if (trigger?.type === "schedule") {
        const minutes = trigger?.config?.everyMinutes;
        return minutes ? `${base} · every ${minutes} min` : base;
    }
    return trigger?.config?.field ? `${base} · ${trigger.config.field}` : base;
};

export const ACTION_TYPES = [
    { key: "update_field", label: "Update a field" },
    { key: "send_email", label: "Send an email" },
    { key: "webhook", label: "Call a webhook" },
    { key: "create_record", label: "Create a record in another object" },
];

export const actionLabel = (type) =>
    ACTION_TYPES.find((a) => a.key === type)?.label || type;

/** Human-readable one-liner for a configured action. */
export function actionSummary(action, fields = [], objects = []) {
    if (!action) return "—";
    if (action.type === "update_field") {
        const label =
            fields.find((f) => f.key === action.field)?.label || action.field;
        return `Set ${label || "a field"} to “${action.value ?? ""}”`;
    }
    if (action.type === "send_email") {
        const to = action.to === "address" ? action.address : "record owner";
        return `Email ${to || "…"} · “${action.subject || ""}”`;
    }
    if (action.type === "webhook") return `POST ${action.url || "…"}`;
    if (action.type === "create_record") {
        const target = objects.find((o) => o.id === action.objectId)?.name;
        return target ? `Create in ${target}` : "Pick an object";
    }
    return action.type;
}

/** Display a record's raw value according to its field's type. */
export function formatFieldValue(field, value) {
    if (value === null || value === undefined || value === "") return "—";
    switch (field?.type) {
        case "currency":
            return formatCurrency(value);
        case "date":
            return formatDate(value);
        case "datetime":
            return formatDateTime(value);
        case "boolean":
            return value === true || value === "true" ? "Yes" : "No";
        /* List endpoints don't carry relation labels, so a table cell can only
           show a short reference — the record drawer resolves the real name. */
        case "relation":
            return `#${String(value).slice(0, 8)}`;
        default:
            return String(value);
    }
}

/** Best-effort display name for an assignable user row. */
export const userName = (u) =>
    u?.name ||
    [u?.firstName, u?.first_name, u?.lastName, u?.last_name]
        .filter(Boolean)
        .join(" ") ||
    u?.email ||
    "User";

export const apiError = (err, fallback) =>
    err?.response?.data?.error || err?.response?.data?.message || fallback;
